<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, addMonths } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import { useLocale } from 'shared/composables/useLocale';
import AiUsageAPI from 'dashboard/api/aiUsage';
import { formatCurrencyAmount } from 'dashboard/constants/billing';
import TokenUsageChart from 'shared/components/charts/TokenUsageChart.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';
import ReportMetricCard from './components/ReportMetricCard.vue';

const MONTH_FORMAT = 'yyyy-MM';

const { t } = useI18n();
const { resolvedLocale } = useLocale();

const month = ref(new Date());
const usage = ref(null);
const isFetching = ref(false);

const monthLabel = computed(() =>
  new Intl.DateTimeFormat(resolvedLocale.value, {
    month: 'long',
    year: 'numeric',
  }).format(month.value)
);
const isCurrentMonth = computed(
  () => format(month.value, MONTH_FORMAT) === format(new Date(), MONTH_FORMAT)
);

const formatTokens = value =>
  new Intl.NumberFormat(resolvedLocale.value).format(value || 0);

const priceDescription = computed(() => {
  if (!usage.value) return '';
  return t('AI_USAGE_REPORTS.PRICE', {
    price: formatCurrencyAmount(usage.value.price_per_unit, 'brl', {
      maximumFractionDigits: 4,
    }),
    unit: formatTokens(usage.value.token_unit),
  });
});

const chartLabels = computed(() => ({
  included: t('AI_USAGE_REPORTS.LEGEND.INCLUDED'),
  excess: t('AI_USAGE_REPORTS.LEGEND.EXCESS'),
  dailyIncluded: t('AI_USAGE_REPORTS.LEGEND.DAILY_INCLUDED'),
  tokens: t('AI_USAGE_REPORTS.AXIS.TOKENS'),
  day: t('AI_USAGE_REPORTS.AXIS.DAY'),
  aria: t('AI_USAGE_REPORTS.CHART_LABEL'),
}));

const fetchUsage = async () => {
  isFetching.value = true;
  try {
    const { data } = await AiUsageAPI.get(format(month.value, MONTH_FORMAT));
    usage.value = data;
  } catch {
    usage.value = null;
    useAlert(t('AI_USAGE_REPORTS.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const changeMonth = amount => {
  month.value = addMonths(month.value, amount);
  fetchUsage();
};

onMounted(fetchUsage);
</script>

<template>
  <ReportHeader
    :header-title="$t('AI_USAGE_REPORTS.HEADER')"
    :header-description="$t('AI_USAGE_REPORTS.DESCRIPTION')"
  />
  <div class="flex flex-col gap-4">
    <div class="flex items-center gap-2">
      <Button
        icon="i-lucide-chevron-left"
        variant="ghost"
        color="slate"
        size="sm"
        :aria-label="$t('AI_USAGE_REPORTS.PREVIOUS_MONTH')"
        @click="changeMonth(-1)"
      />
      <span class="text-heading-3 text-n-slate-12 first-letter:uppercase">
        {{ monthLabel }}
      </span>
      <Button
        icon="i-lucide-chevron-right"
        variant="ghost"
        color="slate"
        size="sm"
        :disabled="isCurrentMonth"
        :aria-label="$t('AI_USAGE_REPORTS.NEXT_MONTH')"
        @click="changeMonth(1)"
      />
    </div>

    <woot-loading-state
      v-if="isFetching && !usage"
      :message="$t('AI_USAGE_REPORTS.LOADING')"
    />

    <template v-if="usage">
      <div
        class="flex flex-wrap gap-4 mx-0 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
      >
        <ReportMetricCard
          :label="$t('AI_USAGE_REPORTS.METRIC.TOTAL_TOKENS.LABEL')"
          :info-text="$t('AI_USAGE_REPORTS.METRIC.TOTAL_TOKENS.TOOLTIP')"
          :value="formatTokens(usage.total_tokens)"
          class="flex-1"
        />
        <ReportMetricCard
          :label="$t('AI_USAGE_REPORTS.METRIC.DAILY_INCLUDED_TOKENS.LABEL')"
          :info-text="
            $t('AI_USAGE_REPORTS.METRIC.DAILY_INCLUDED_TOKENS.TOOLTIP')
          "
          :value="formatTokens(usage.daily_included_tokens)"
          class="flex-1"
        />
        <ReportMetricCard
          :label="$t('AI_USAGE_REPORTS.METRIC.EXCESS_TOKENS.LABEL')"
          :info-text="$t('AI_USAGE_REPORTS.METRIC.EXCESS_TOKENS.TOOLTIP')"
          :value="formatTokens(usage.excess_tokens)"
          class="flex-1"
        />
        <ReportMetricCard
          :label="$t('AI_USAGE_REPORTS.METRIC.AMOUNT.LABEL')"
          :info-text="priceDescription"
          :value="formatCurrencyAmount(usage.amount, 'brl')"
          class="flex-1"
        />
      </div>

      <div
        class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
      >
        <h3 class="mb-4 text-heading-3 text-n-slate-12">
          {{ $t('AI_USAGE_REPORTS.CHART_TITLE') }}
        </h3>
        <TokenUsageChart
          v-if="usage.total_tokens"
          :days="usage.days"
          :daily-included="usage.daily_included_tokens"
          :labels="chartLabels"
          :locale="resolvedLocale"
        />
        <div v-else class="flex items-center justify-center h-72">
          <span class="text-sm text-n-slate-10">
            {{ $t('AI_USAGE_REPORTS.NO_USAGE') }}
          </span>
        </div>
      </div>
    </template>
  </div>
</template>
